import { useCallback, useState } from 'react';
import { ArrowLeft, Package, Warehouse, WarningCircle } from 'shared/icons';
import api from '../../../services/api';
import { FormatInt } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Id, Mono, Sku } from '../../ui/Mono';
import { OccupancyBadgeVariant, StatusBadgeVariant } from '../locationFields';
import { FormatLpnProductCount } from '../warehouseLabels';
import BlockTower from './BlockTower';
import { CodeLine, PlainAddress, StepShell } from './CollectorChrome';
import ScanInput from './ScanInput';
import { PlayScanError, PlayScanSuccess } from './collectorFeedback';
import {
    FormatLocationPlain,
    IsPositionOccupied,
    ParseScanResult,
    SCAN_TYPE_BLOCK,
    SCAN_TYPE_LOCATION,
    SCAN_TYPE_LPN,
} from './collectorScan';

/**
 * CONSULTA LIVRE (§8.2 "modo consulta livre"): bipe qualquer coisa e a tela
 * diz o que é e o que tem dentro.
 *
 * É o modo mais usado do coletor no dia a dia e o mais simples de todos: não
 * tem passo, não tem esperado, não tem erro de casamento — só classificação
 * (`POST /warehouse/scan`, o ÚNICO parsing do sistema, §2.2) e leitura. O
 * único "erro" possível é UNKNOWN, e mesmo esse é informação, não falha.
 *
 * A resposta é sempre a mesma tríade do fluxo guiado: o que é (grande, em
 * texto claro), o código (monoespaçado, subordinado) e o CONTEÚDO.
 */

const SectionTitle = ({ icon: IconComponent, children, count = null }) => (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
        <IconComponent size={18} className="text-mono-500" />
        {children}
        {count !== null ? (
            <span className="text-xs font-normal text-mono-500 dark:text-mono-400">({FormatInt(count)})</span>
        ) : null}
    </h3>
);

const EmptyLine = ({ children }) => (
    <p className="rounded-lg border border-dashed border-mono-300 px-3 py-4 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
        {children}
    </p>
);

const FreeQueryPanel = ({ label, onExit }) => {
    const [result, setResult] = useState(null);
    const [rawCode, setRawCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleScan = useCallback(
        async (code) => {
            setLoading(true);
            setErrorMessage(null);
            setRawCode(code);
            try {
                const payload = await api.scanWarehouseCode(code);
                const parsed = ParseScanResult(payload);
                setResult(parsed);
                if (parsed.type === SCAN_TYPE_LOCATION || parsed.type === SCAN_TYPE_BLOCK || parsed.type === SCAN_TYPE_LPN) {
                    PlayScanSuccess();
                } else {
                    PlayScanError();
                }
            } catch (error) {
                PlayScanError();
                setResult(null);
                setErrorMessage(error?.message || label('wms.common.load_error'));
            } finally {
                setLoading(false);
            }
        },
        [label],
    );

    const plain = (location) => FormatLocationPlain(location, { label });

    return (
        <StepShell
            title={label('wms.collector.free_query_title')}
            footer={
                <Button variant="secondary" size="lg" className="w-full" onClick={onExit}>
                    <ArrowLeft size={20} className="mr-2" />
                    {label('wms.collector.back_to_menu')}
                </Button>
            }
        >
            <ScanInput
                label={label}
                onScan={handleScan}
                disabled={loading}
                focusKey={`free-${rawCode}`}
                hint={label('wms.collector.free_query_hint')}
            />

            {errorMessage ? (
                <div
                    role="alert"
                    className="flex items-center gap-2 rounded-xl bg-danger-soft p-4 text-base text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                >
                    <WarningCircle size={22} className="shrink-0" />
                    <span>{errorMessage}</span>
                </div>
            ) : null}

            {result?.type === SCAN_TYPE_LOCATION && result.location ? (
                <div className="space-y-4 rounded-2xl border-2 border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <div className="space-y-1">
                        <p className="text-center text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.collector.is_a_location')}
                        </p>
                        <PlainAddress>{plain(result.location)}</PlainAddress>
                        <CodeLine>{result.location.code}</CodeLine>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                        <Badge>{label(`wms.location_type.${result.location.type}`)}</Badge>
                        <Badge variant={StatusBadgeVariant(result.location.status)}>
                            {label(`wms.location_status.${result.location.status}`)}
                        </Badge>
                        <Badge variant={OccupancyBadgeVariant(result.location.occupancy || 'FREE')}>
                            {label(`wms.occupancy.${result.location.occupancy || 'FREE'}`)}
                        </Badge>
                    </div>
                    <div className="space-y-2">
                        <SectionTitle icon={Package} count={result.lpns.length}>
                            {label('wms.collector.lpns_here')}
                        </SectionTitle>
                        {result.lpns.length === 0 ? (
                            <EmptyLine>{label('wms.panel.no_lpns_here')}</EmptyLine>
                        ) : (
                            <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                {result.lpns.map((row) => (
                                    <li
                                        key={row.id || row.code}
                                        className="flex flex-wrap items-center gap-2 py-2.5"
                                    >
                                        <Id value={row.code} className="text-base font-bold" />
                                        <span className="ml-auto text-sm text-mono-500 dark:text-mono-400">
                                            {FormatLpnProductCount(row.item_count, label)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            ) : null}

            {result?.type === SCAN_TYPE_BLOCK && result.block ? (
                <div className="space-y-4 rounded-2xl border-2 border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <div className="space-y-1">
                        <p className="text-center text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.collector.is_a_block')}
                        </p>
                        <PlainAddress>
                            {`${label('wms.field.rack')} ${result.block.rack}, ${label('wms.field.block')} ${result.block.bay}`}
                        </PlainAddress>
                        <CodeLine>{result.block.code}</CodeLine>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                        <Badge variant="neutral">
                            {`${FormatInt(result.positions.length)} ${label('wms.cascade.positions_suffix')}`}
                        </Badge>
                        <Badge variant="warning">
                            {`${FormatInt(result.positions.filter(IsPositionOccupied).length)} ${label('wms.occupancy.OCCUPIED')}`}
                        </Badge>
                    </div>
                    <BlockTower
                        rack={result.block.rack}
                        bay={result.block.bay}
                        positions={result.positions}
                        label={label}
                    />
                </div>
            ) : null}

            {result?.type === SCAN_TYPE_LPN && result.lpn ? (
                <div className="space-y-4 rounded-2xl border-2 border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                    <div className="space-y-1">
                        <p className="text-center text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.collector.is_a_lpn')}
                        </p>
                        <PlainAddress>
                            <Mono>{result.lpn.code}</Mono>
                        </PlainAddress>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                        <Badge variant={result.lpn.status === 'CLOSED' ? 'neutral' : 'success'}>
                            {label(`wms.lpn_status.${result.lpn.status}`)}
                        </Badge>
                        {result.lpn.is_bulky ? <Badge variant="warning">{label('wms.lpn.field.is_bulky')}</Badge> : null}
                    </div>

                    <div className="space-y-1 rounded-xl bg-mono-50 px-3 py-3 text-center dark:bg-mono-900">
                        <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            {label('wms.collector.lpn_is_at')}
                        </p>
                        <p className="text-xl font-bold text-black dark:text-white">
                            {result.lpn.location
                                ? plain(result.lpn.location)
                                : result.lpn.location_code || label('wms.collector.lpn_no_location')}
                        </p>
                        {result.lpn.location_code || result.lpn.location?.code ? (
                            <CodeLine>{result.lpn.location_code || result.lpn.location.code}</CodeLine>
                        ) : null}
                    </div>

                    <div className="space-y-2">
                        <SectionTitle icon={Warehouse} count={result.items.length}>
                            {label('wms.lpn.field.items')}
                        </SectionTitle>
                        {result.items.length === 0 ? (
                            <EmptyLine>{label('wms.lpns.items_empty')}</EmptyLine>
                        ) : (
                            <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                {result.items.map((item, index) => (
                                    <li
                                        key={item.id || `${item.sku}-${index}`}
                                        className="flex items-start gap-3 py-2.5"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <Sku value={item.sku} className="text-base font-bold" />
                                            {item.product_name ? (
                                                <p className="truncate text-sm text-mono-500 dark:text-mono-400">
                                                    {item.product_name}
                                                </p>
                                            ) : null}
                                        </div>
                                        <Mono className="shrink-0 text-xl font-black text-black dark:text-white">
                                            {FormatInt(item.qty ?? 0)}
                                        </Mono>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            ) : null}

            {result && ![SCAN_TYPE_LOCATION, SCAN_TYPE_BLOCK, SCAN_TYPE_LPN].includes(result.type) ? (
                <div className="space-y-3 rounded-2xl border-2 border-warning-400 bg-warning-soft p-5 text-center dark:border-warning-700 dark:bg-warning-900/30">
                    <WarningCircle size={48} className="mx-auto text-warning-text dark:text-warning-300" />
                    <p className="text-2xl font-black text-warning-text dark:text-warning-200">
                        {label('wms.collector.unknown_title')}
                    </p>
                    <Mono className="block break-all text-lg font-bold text-warning-text dark:text-warning-300">
                        {rawCode}
                    </Mono>
                    <p className="text-sm text-warning-text dark:text-warning-300">
                        {label('wms.collector.unknown_hint')}
                    </p>
                </div>
            ) : null}

            {!result && !errorMessage && !loading ? (
                <EmptyLine>{label('wms.collector.free_query_idle')}</EmptyLine>
            ) : null}
        </StepShell>
    );
};

export default FreeQueryPanel;
